import type { FhirResourceTypeString } from '@ctw/shared/api/fhir/types';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getAiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { prettyJsonStringifyObjectWithErrors } from '@ctw/shared/utils/json';
import { QUERY_KEY_PATIENT_SUMMARY_JOB_POLL } from '@ctw/shared/utils/query-keys';
import type { AnyJsonObj } from '@ctw/shared/utils/types';
import { parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MergeExclusive } from 'type-fest';

// Statuses the API can return for a patient summary job
export type PatientSummaryJobApiStatus = 'running' | 'completed' | 'error';
// Extended status with UI-only states
export type PatientSummaryStatus =
  | PatientSummaryJobApiStatus
  | 'insufficient-data'
  | 'limit-reached';

export interface PatientSummaryJobCitation {
  id: number;
  resourceType: FhirResourceTypeString;
  resourceId: string;
  uid6: string;
}

interface PatientSummaryJobsApiFatalError {
  statusCode: string;
  statusText: string;
  statusDetails: string;
}

const isPatientSummaryJobsApiFatalError = (
  job: AnyJsonObj,
): job is PatientSummaryJobsApiFatalError => {
  return (
    Object.hasOwn(job, 'statusCode') &&
    Object.hasOwn(job, 'statusText') &&
    Object.hasOwn(job, 'statusDetails')
  );
};

interface PatientSummaryJobApiDataCommon {
  title: string;
  content: string;
}

interface PatientSummaryJobApiDataSuccess extends PatientSummaryJobApiDataCommon {
  status: Extract<PatientSummaryJobApiStatus, 'running' | 'completed'>;
  citations: Array<PatientSummaryJobCitation>;
}

interface PatientSummaryJobApiDataError extends PatientSummaryJobApiDataCommon {
  status: Extract<PatientSummaryJobApiStatus, 'error'>;
  errorMessage: string;
}

type PatientSummaryJobApiData = MergeExclusive<
  PatientSummaryJobApiDataSuccess,
  PatientSummaryJobApiDataError
>;

export const isPatientSummaryJobApiDataSuccess = (
  job: AnyJsonObj,
): job is PatientSummaryJobApiDataSuccess => {
  return ['running', 'completed'].includes(job.status);
};

export const isPatientSummaryJobApiDataError = (
  job: AnyJsonObj,
): job is PatientSummaryJobApiDataError => {
  return job.status === 'error';
};

export interface PatientSummaryJobsApiResponseSuccess {
  data: {
    type: 'summary-jobs';
    id: string;
    attributes: {
      status: PatientSummaryJobApiStatus;
      summary: Array<PatientSummaryJobApiData>;
      createdAt: string;
      updatedAt: string;
      summaryExpiresAt: string;
      errorCode?: number;
      errorTitle?: string;
      errorDetail?: string;
    };
    relationships: {
      patient: {
        data: {
          type: string;
          id: string;
        };
      };
    };
  };
}

interface PatientSummaryJobsApiResponseError {
  errors: Array<{
    status: Extract<PatientSummaryJobApiStatus, 'running' | 'completed'>;
    title: string;
    detail: string;
  }>;
}

type PatientSummaryJobsApiResponse = MergeExclusive<
  PatientSummaryJobsApiResponseSuccess,
  PatientSummaryJobsApiResponseError
>;

export const isPatientSummaryJobsApiResponseSuccess = (
  response: AnyJsonObj,
): response is PatientSummaryJobsApiResponseSuccess => {
  return response.data !== undefined;
};

export const isPatientSummaryJobsApiResponseError = (
  response: AnyJsonObj,
): response is PatientSummaryJobsApiResponseError => {
  return response.errors !== undefined;
};

// The AI service returns rate-limit / capacity errors as an HTTP 200 with a body of
// shape `{ status_code: number, detail: string, headers: null }` instead of using the
// HTTP status line. Detect that shape and hoist `status_code` so downstream handling
// can treat it like any other fatal error.
interface CustomStatusCodeErrorBody {
  status_code: number;
  detail?: string;
}
const isCustomStatusCodeErrorBody = (body: AnyJsonObj): body is CustomStatusCodeErrorBody =>
  typeof body.status_code === 'number';

const INSUFFICIENT_DATA_ERROR_DETAIL_PATTERN = /not enough data points/i;

const CACHED_SUMMARY_HEAD_TIMEOUT_MS = 10_000;

// The summarization backend signals "not enough patient chart data to generate a summary"
// by returning a polling response with `attributes.status === 'error'`, `errorTitle` of
// `'generation_error'`, and an `errorDetail` string of the form "Not enough data points: N < M".
// We intercept this specific combination and surface it as the 'insufficient-data' hook status
// so the UI can render a calm "check back shortly" treatment instead of a generic error.
const isInsufficientDataJobResponse = (response: AnyJsonObj): boolean => {
  if (!isPatientSummaryJobsApiResponseSuccess(response)) {
    return false;
  }
  const attributes = response.data.attributes;
  return (
    attributes.status === 'error' &&
    attributes.errorTitle === 'generation_error' &&
    typeof attributes.errorDetail === 'string' &&
    INSUFFICIENT_DATA_ERROR_DETAIL_PATTERN.test(attributes.errorDetail)
  );
};

export interface UsePatientSummariesOptions {
  /** Patient UPID. Required when autoSubmit is true. */
  patientUpid?: string;
  /** When false, the hook will not auto-submit a job on mount. Call `submitJob(patientUpid)` to trigger manually. Defaults to true. */
  autoSubmit?: boolean;
}

export interface UsePatientSummariesResponse {
  summaryJobId?: string;
  status: PatientSummaryStatus;
  summaries: Array<PatientSummaryJobApiData>;
  lastRefreshed?: Date;
  error?: PatientSummaryJobsApiFatalError | PatientSummaryJobsApiResponseError;
  /** Manually trigger a summary job submission. Provide the patient UPID for the job. */
  submitJob: (patientUpid: string) => void;
  /** Reset the hook state, stopping any in-progress polling. */
  reset: () => void;
  /** Check for a cached summary via HEAD request. Returns the cached status or null if not found. */
  checkCachedSummary: (patientUpid: string) => Promise<'running' | 'completed' | null>;
  /** Delete a cached summary job by id. Resets local state on success. */
  deleteSummaryJob: (jobId: string) => Promise<boolean>;
}

export const usePatientSummaries = (
  options?: UsePatientSummariesOptions,
): UsePatientSummariesResponse => {
  const autoSubmit = options?.autoSubmit !== false;
  const telemetry = useTelemetry();
  const { requestContext } = useCTW();

  const [summaryJobsResponse, setSummaryJobsResponse] = useState<
    PatientSummaryJobsApiFatalError | PatientSummaryJobsApiResponse | null
  >(null);
  const [activeUpid, setActiveUpid] = useState<string | undefined>(options?.patientUpid);
  const submitInitiatedRef = useRef(false);

  const submitPatientSummaryJob = useCallback(
    async (
      patientUpid: string,
    ): Promise<PatientSummaryJobsApiFatalError | PatientSummaryJobsApiResponse> => {
      try {
        const response = await fetch(
          `${getAiServiceBaseUrl({ env: requestContext.env })}/summary-jobs`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${requestContext.authToken}`,
              'Zus-Account': requestContext.builderId,
            },
            body: JSON.stringify({
              data: {
                relationships: {
                  patient: {
                    data: {
                      id: patientUpid,
                    },
                  },
                },
              },
            }),
          },
        );

        const responseText = await response.text();

        if (!response.ok) {
          return {
            statusCode: response.status.toString(),
            statusText: response.statusText,
            statusDetails: responseText,
          };
        }

        let responseJson: AnyJsonObj | null = null;
        try {
          responseJson = JSON.parse(responseText);
        } catch {
          /* pass */
        }

        if (!responseJson) {
          return {
            statusCode: 'invalid_non_json_response',
            statusText: 'Non-JSON response from server',
            statusDetails: responseText,
          };
        }

        if (isCustomStatusCodeErrorBody(responseJson)) {
          return {
            statusCode: responseJson.status_code.toString(),
            statusText: responseJson.detail ?? '',
            statusDetails: responseText,
          };
        }

        if (
          isPatientSummaryJobsApiResponseSuccess(responseJson) ||
          isPatientSummaryJobsApiResponseError(responseJson)
        ) {
          return responseJson;
        }

        return {
          statusCode: 'invalid_json_response',
          statusText: 'JSON response from server did not match expected schema',
          statusDetails: responseText,
        };
      } catch (error: unknown) {
        const errorMessage = 'Unknown Exception submitting patient summary job';
        telemetry.trackError({
          message: errorMessage,
          error: error instanceof Error ? error : new Error(errorMessage),
          context: {
            error,
            upid: patientUpid,
          },
        });

        return {
          statusCode: 'unknown_exception',
          statusText: error instanceof Error ? error.name : 'unknown',
          statusDetails:
            error instanceof Error ? error.message : prettyJsonStringifyObjectWithErrors(error),
        };
      }
    },
    [requestContext, telemetry],
  );

  const currentJobId =
    summaryJobsResponse !== null && isPatientSummaryJobsApiResponseSuccess(summaryJobsResponse)
      ? summaryJobsResponse.data.id
      : undefined;

  const summaryJobPollingQuery = useCtwQuery({
    // Enable when we have a running job, or we're trying to hydrate a
    // completed by empty cached summary
    enabled:
      summaryJobsResponse !== null &&
      isPatientSummaryJobsApiResponseSuccess(summaryJobsResponse) &&
      (summaryJobsResponse.data.attributes.status === 'running' ||
        (summaryJobsResponse.data.attributes.status === 'completed' &&
          summaryJobsResponse.data.attributes.summary.length === 0)),
    refetchInterval: 1000,
    refetchIntervalInBackground: true,
    queryId: QUERY_KEY_PATIENT_SUMMARY_JOB_POLL,
    queryKey: [currentJobId, activeUpid],
    queryFn: async ({ requestContext: queryRequestContext }) => {
      if (
        summaryJobsResponse !== null &&
        isPatientSummaryJobsApiResponseSuccess(summaryJobsResponse)
      ) {
        try {
          const response = await fetch(
            `${getAiServiceBaseUrl({ env: queryRequestContext.env })}/summary-jobs/${summaryJobsResponse.data.id}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${queryRequestContext.authToken}`,
                'Zus-Account': queryRequestContext.builderId,
              },
            },
          );

          const responseText = await response.text();

          if (!response.ok) {
            return {
              statusCode: response.status.toString(),
              statusText: response.statusText,
              statusDetails: responseText,
            };
          }

          let responseJson: AnyJsonObj | null = null;
          try {
            responseJson = JSON.parse(responseText);
          } catch {
            /* pass */
          }

          if (!responseJson) {
            return {
              statusCode: 'invalid_non_json_response',
              statusText: 'Non-JSON response from server',
              statusDetails: responseText,
            };
          }

          if (
            isPatientSummaryJobsApiResponseSuccess(responseJson) ||
            isPatientSummaryJobsApiResponseError(responseJson)
          ) {
            return responseJson;
          }

          return {
            statusCode: 'invalid_json_response',
            statusText: 'JSON response from server did not match expected schema',
            statusDetails: responseText,
          };
        } catch (error: unknown) {
          const errorMessage = 'Unknown Exception polling patient summary job';
          telemetry.trackError({
            message: errorMessage,
            error: error instanceof Error ? error : new Error(errorMessage),
            context: {
              error,
              upid: activeUpid,
              summaryJobId: summaryJobsResponse.data.id,
            },
          });

          return {
            statusCode: 'unknown_exception',
            statusText: error instanceof Error ? error.name : 'unknown',
            statusDetails:
              error instanceof Error ? error.message : prettyJsonStringifyObjectWithErrors(error),
          };
        }
      }

      return undefined;
    },
  });

  const submitAbortControllerRef = useRef<AbortController | null>(null);

  const submitJob = useCallback(
    (patientUpid: string) => {
      // Abort any in-flight submission to prevent stale responses from overwriting state.
      submitAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      submitAbortControllerRef.current = abortController;

      setSummaryJobsResponse(null);
      setActiveUpid(patientUpid);
      submitPatientSummaryJob(patientUpid).then((response) => {
        if (!abortController.signal.aborted) {
          setSummaryJobsResponse(response);
        }
      });
    },
    [submitPatientSummaryJob],
  );

  const reset = useCallback(() => {
    setSummaryJobsResponse(null);
    setActiveUpid(undefined);
    submitInitiatedRef.current = false;
  }, []);

  const deleteSummaryJob = useCallback(
    async (jobId: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `${getAiServiceBaseUrl({ env: requestContext.env })}/summary-jobs/${jobId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Zus-Account': requestContext.builderId,
            },
          },
        );

        if (!response.ok) {
          return false;
        }

        setSummaryJobsResponse(null);
        return true;
      } catch {
        return false;
      }
    },
    [requestContext],
  );

  const checkCachedSummary = useCallback(
    async (patientUpid: string): Promise<'running' | 'completed' | null> => {
      // Reset any existing job state (stops polling for a previous patient).
      setSummaryJobsResponse(null);
      setActiveUpid(patientUpid);

      try {
        const response = await fetch(
          `${getAiServiceBaseUrl({ env: requestContext.env })}/summary-jobs?upid=${patientUpid}`,
          {
            method: 'HEAD',
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Zus-Account': requestContext.builderId,
            },
            // Cap the cache check duration to avoid hanging UI
            signal: AbortSignal.timeout(CACHED_SUMMARY_HEAD_TIMEOUT_MS),
          },
        );

        if (response.status === 404) {
          return null;
        }

        if (response.ok) {
          const jobId = response.headers.get('X-Job-Id');
          const jobStatus = response.headers.get('X-Job-Status') as 'running' | 'completed' | null;

          if (jobId && (jobStatus === 'completed' || jobStatus === 'running')) {
            setActiveUpid(patientUpid);
            setSummaryJobsResponse({
              data: {
                type: 'summary-jobs',
                id: jobId,
                attributes: {
                  status: jobStatus,
                  summary: [],
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  summaryExpiresAt: new Date().toISOString(),
                },
                relationships: {
                  patient: { data: { type: 'fhir/Patient', id: patientUpid } },
                },
              },
            });
            return jobStatus;
          }
        }
      } catch (error: unknown) {
        const isTimeout = error instanceof DOMException && error.name === 'TimeoutError';
        const errorMessage = isTimeout
          ? 'Timed out checking for cached patient summary'
          : 'Error checking for cached patient summary';
        telemetry.trackError({
          message: errorMessage,
          error: error instanceof Error ? error : new Error(errorMessage),
          context: {
            error,
            upid: patientUpid,
            timedOut: isTimeout,
          },
        });
      }

      return null;
    },
    [requestContext, telemetry],
  );

  useEffect(() => {
    if (
      autoSubmit &&
      options?.patientUpid &&
      summaryJobsResponse === null &&
      !submitInitiatedRef.current
    ) {
      submitInitiatedRef.current = true;
      setActiveUpid(options.patientUpid);
      submitPatientSummaryJob(options.patientUpid).then((response) =>
        setSummaryJobsResponse(response),
      );
    } else if (summaryJobPollingQuery.data !== undefined) {
      setSummaryJobsResponse(summaryJobPollingQuery.data);
    }
  }, [
    autoSubmit,
    options?.patientUpid,
    submitPatientSummaryJob,
    summaryJobPollingQuery.data,
    summaryJobsResponse,
  ]);

  return useMemo(() => {
    if (summaryJobsResponse === null) {
      return {
        status: 'running' as const,
        summaries: [],
        submitJob,
        reset,
        checkCachedSummary,
        deleteSummaryJob,
      };
    }

    // Fatal error submitting jobs request
    if (isPatientSummaryJobsApiFatalError(summaryJobsResponse)) {
      // The backend signals "monthly summary limit exhausted and no cached summary
      // to fall back on" with a 429 on the POST /summary-jobs call. Surface it as
      // its own UI status so the calling component can render the lock-panel state
      // without needing to cross-reference client-side usage counters.
      if (summaryJobsResponse.statusCode === '429') {
        return {
          status: 'limit-reached',
          summaries: [],
          submitJob,
          reset,
          checkCachedSummary,
          deleteSummaryJob,
        } satisfies UsePatientSummariesResponse;
      }
      return {
        status: 'error',
        summaries: [],
        error: summaryJobsResponse,
        submitJob,
        reset,
        checkCachedSummary,
        deleteSummaryJob,
      } satisfies UsePatientSummariesResponse;
    }

    // Response error submitting jobs request
    if (isPatientSummaryJobsApiResponseError(summaryJobsResponse)) {
      return {
        status: 'error',
        summaries: [],
        error: summaryJobsResponse,
        submitJob,
        reset,
        checkCachedSummary,
        deleteSummaryJob,
      } satisfies UsePatientSummariesResponse;
    }

    // Prefer polling data when available; fall back to the initial POST response
    // (e.g. when the API returned an already-completed cached job and polling never started).
    const latestResponse = summaryJobPollingQuery.data ?? summaryJobsResponse;

    // Fatal error polling job endpoint
    if (isPatientSummaryJobsApiFatalError(latestResponse)) {
      return {
        status: 'error',
        summaries: [],
        error: latestResponse,
        submitJob,
        reset,
        checkCachedSummary,
        deleteSummaryJob,
      } satisfies UsePatientSummariesResponse;
    }

    // Response error polling job endpoint
    if (isPatientSummaryJobsApiResponseError(latestResponse)) {
      return {
        status: 'error',
        summaries: [],
        error: latestResponse,
        submitJob,
        reset,
        checkCachedSummary,
        deleteSummaryJob,
      } satisfies UsePatientSummariesResponse;
    }

    // Patient chart doesn't have enough data points yet — surfaced as its own status so the
    // UI can render a calm "check back shortly" state instead of a generic error.
    if (isInsufficientDataJobResponse(latestResponse)) {
      return {
        summaryJobId: latestResponse.data.id,
        status: 'insufficient-data',
        summaries: [],
        lastRefreshed: parseISO(latestResponse.data.attributes.updatedAt),
        submitJob,
        reset,
        checkCachedSummary,
        deleteSummaryJob,
      } satisfies UsePatientSummariesResponse;
    }

    return {
      summaryJobId: latestResponse.data.id,
      status: latestResponse.data.attributes.status,
      summaries: latestResponse.data.attributes.summary,
      lastRefreshed: parseISO(latestResponse.data.attributes.updatedAt),
      submitJob,
      reset,
      checkCachedSummary,
      deleteSummaryJob,
    } satisfies UsePatientSummariesResponse;
  }, [
    summaryJobsResponse,
    summaryJobPollingQuery,
    submitJob,
    reset,
    checkCachedSummary,
    deleteSummaryJob,
  ]);
};
